import { Empty } from 'antd';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';

import { Box, Stack, CircularProgress } from '@mui/material';

import { matchEmployeeFromToken } from 'src/utils/welfareAccess';
import {
  canAccessPage,
  pageKeyFromPath,
  isPageAccessAdmin,
  isHardcodedAllowed,
} from 'src/utils/pageAccess';

import { useDataContext } from 'src/context';

export default function PageAccessGate({ pageKey, adminOnly, children }) {
  const { data } = useDataContext();
  const [cookies] = useCookies(['session']);
  const { pathname } = useLocation();
  const key = pageKey || pageKeyFromPath(pathname);

  // Unmapped routes (Home, ของฉัน, รายงาน, …) must not wait on employee list.
  if (!adminOnly && !key) return children;

  const employees = data?.getAllEmployees;
  if (!employees?.length) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  let decoded = null;
  try {
    if (cookies?.session && typeof cookies.session === 'string') decoded = jwtDecode(cookies.session);
  } catch {
    decoded = null;
  }

  const emp = matchEmployeeFromToken(employees, decoded);
  if (adminOnly) {
    return isPageAccessAdmin(emp) ? children : deny();
  }

  if (isHardcodedAllowed(emp, key)) return children;

  // ponytail: null = grants not fetched yet; [] = fetched, no extra rules
  const grants = data?.pageGrants;
  if (!Array.isArray(grants)) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  return canAccessPage(emp, key, grants) ? children : deny();
}

function deny() {
  return (
    <Empty
      description={
        <Box sx={{ fontFamily: 'Noto Sans Thai' }}>คุณไม่มีสิทธิ์เข้าถึงหน้านี้</Box>
      }
    />
  );
}

PageAccessGate.propTypes = {
  adminOnly: PropTypes.bool,
  children: PropTypes.node,
  pageKey: PropTypes.string,
};
